import { ProductRatingService } from './../../../../providers/services/product-rating.service';
import { ProductRatingRequest } from './../../../../models/product-rating-request';
import { Input, OnInit, Directive, inject } from '@angular/core';
import { ProductUrlService } from '../../../../common/seo/product-url.service';
import { ProductBasicResponse, Company } from '@pdp/common';
import { ProductQuantity } from '../../../../models/product-quantity';
import { NavigationExtras, Router, ActivatedRoute } from '@angular/router';
import { ProductService } from '../../../../providers/services/product.service';
import { CartService } from '../../../../providers/services/cart.service';
import { WishilistService } from '../../../../providers/services/wishilist.service';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { QuickViewComponent } from './quick-view/quick-view.component';
import { takeUntil } from 'rxjs/operators';
import { OrderItemRequest } from '../../../../models/order-item-request';
import { Subject } from 'rxjs';
import { AppComponent } from '../../../../app.component';
import { PlatformToastMessage, PlatformToastMessageType } from '@pdp/common';

@Directive()
export abstract class AbstractProductItem implements OnInit {
    private unsubscribe: Subject<void> = new Subject();

    @Input()
    public company: Company;
    @Input()
    public product: ProductBasicResponse;
    @Input()
    public isFeatured: boolean;
    /**
     * Position of this product in the list (zero-based).
     * Used to apply loading="eager" + fetchpriority="high" to above-the-fold cards
     * and loading="lazy" to cards below the fold. Defaults to 0 if not provided
     * (single-item contexts treat the card as above-the-fold).
     */
    @Input()
    public index: number = 0;
    public primaryImage: string;
    public secondaryImage: string;
    public productQuantity: ProductQuantity;
    public routerLinkUrl: string;
    private dialogRef: MatDialogRef<QuickViewComponent> = null;
    public variantImage: any = '';
    public loading: boolean;

    private productUrlService = inject(ProductUrlService);

    constructor(public router: Router, public activatedRoute: ActivatedRoute, public productsService: ProductService,
        public wishlistService: WishilistService,
        public cartService: CartService,
        public dialog: MatDialog,
        public ratingService: ProductRatingService) {
    }

    public updateRating(rating: number) {
        const request: ProductRatingRequest = new ProductRatingRequest();
        request.productId = this.product.id;
        request.rating = rating;
        this.ratingService.rateProduct(request).pipe(takeUntil(this.unsubscribe)).subscribe(() => {

        });
    }
    public addToCart(quantity: number = 1) {
        if (!(this.product.skuCount > 1)) {
            // i had to assign this.routerLinkUrl to local variable link, otherwise i get Unhandled Navigation Error from angular router w/e
            const link = this.routerLinkUrl;
            const navigationExtras: NavigationExtras = {
                relativeTo: this.activatedRoute,
                queryParams: {
                    'itm': this.product.id
                }
            };
            this.router.navigate([link], navigationExtras)
            // this.router.navigate(['/search/itm' + this.product['product_url']], { queryParams: { itm: this.product.id } })
            return;
        }
        this.loading = true;
        const orderItemRequest: OrderItemRequest = new OrderItemRequest(this.product.id, null, quantity, null);
        orderItemRequest.skuId = this.product.defaultSkuId;

        this.cartService.addProduct(orderItemRequest)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(res => {
                AppComponent.toastSubject.next(new PlatformToastMessage(PlatformToastMessageType.SUCCESS, $localize`:@@UspesnoDodatoUKorpu:Uspešno dodato u korpu`));
                this.loading = false;
            }, () => {
                this.loading = false;
                AppComponent.toastSubject.next(new PlatformToastMessage(PlatformToastMessageType.ERROR, 'Došlo je do greške. Proizvod ' +
                    this.product.name + ' (' + orderItemRequest.quantity + 'kom) nije dodat u korpu!'));

            });
    }
    public addToCompare() {
        this.productsService.addToCompare(this.product);
    }
    public addToWishlist() {
        this.wishlistService.addToWishlist(this.product);
    }
    public changeVariantImage(image) {
        this.variantImage = image;
    }

    ngOnInit() {
        if (this.product) {
            if (this.product['image_url']) {
                this.primaryImage = this.product['image_url'];
            } else {
                this.primaryImage = 'assets/images/img-not-available.png';
            }
            this.productQuantity = this.productsService.computeQuantity(this.product['quantityAvailable'], this.product['salesAvailabilityType']);
        }
        this.routerLinkUrl = this.buildRouterLinkUrl();
    }

    /**
     * Builds an absolute URL anchored to the product's primary category. This
     * replaces the older './itm{slug}' relative path which produced nested URLs
     * (e.g. /cat/itm/prodA/itm/prodB) when the user landed on a product detail
     * page and clicked a recommended product. Now every product link in this
     * card collapses to the same canonical-shaped URL regardless of where it
     * renders.
     *
     * Resolution order:
     *  1. product.categoriesAsArray[0].url (if data has it)
     *  2. Current page URL when we're on a category page (fallback for slim
     *     product list responses that don't include categoriesAsArray)
     *  3. /search/itm{slug} as last resort, matching the old isFeatured branch
     */
    private buildRouterLinkUrl(): string {
        if (this.isFeatured) {
            return '/search/itm' + this.product['product_url'];
        }
        const linkPath = this.productUrlService.getRouterLinkPath(
            this.product, this.deriveCurrentCategoryUrl());
        return linkPath || '/search/itm' + this.product['product_url'];
    }

    private deriveCurrentCategoryUrl(): string | null {
        const url = (this.router.url || '').split('?')[0];
        if (url && url.indexOf('/itm/') === -1 && url !== '/' && url !== '/home') {
            return url;
        }
        return null;
    }

    public openQuickView() {

        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = true;
        dialogConfig.width = '1000px';
        // dialogConfig.maxHeight = '70vh',
        dialogConfig.panelClass = 'parent-modal-class'
        dialogConfig.disableClose = false;
        dialogConfig.data = {
            product: this.product
        };
        this.dialogRef = this.dialog.open(QuickViewComponent, dialogConfig);

        this.dialogRef.afterClosed().pipe(takeUntil(this.unsubscribe)).subscribe(result => {
            if (result) {
            }
        });
    };

    ngOnDestroy() {
        if (this.dialogRef) {
            this.dialogRef.close();
        }
    }
}
